import React, { memo, useCallback, useEffect, useState } from 'react';
import { useMemo } from 'react';
import { Chip, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CommentIcon from '@mui/icons-material/Comment';
import { useResizeDetector } from 'react-resize-detector';
import { ClassNameMap } from '@mui/styles';
import { ReadonlyLabelChip } from 'modules/common/components/Chip';
import { TimesheetLabel } from 'modules/timesheet-labels/labelSchema';
import { LabelType } from 'graphql/types';
import { isLabelType } from 'modules/timesheet-labels/hooks';

type ListLabelsProps = {
  labels: TimesheetLabel[];
  width?: number;
  multiline?: boolean;
  areChipsClickable?: boolean;
  classes?: ClassNameMap;
  overflowOnClick?: (e: React.MouseEvent<HTMLElement>) => void;
  updateCollapseIconVisibility?: (show: boolean) => void;
  removeTopPadding?: boolean;
};

interface ListLabelsStyleProps {
  removeTopPadding: boolean;
  classes?: ClassNameMap;
}

const useListLabelsStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    paddingTop: ({ removeTopPadding }: ListLabelsStyleProps) =>
      removeTopPadding ? theme.spacing(0) : theme.spacing(0.25)
  },
  chip: {
    marginTop: theme.spacing(0.25),
    height: theme.spacing(3)
  }
}));

export const ListLabels: React.FC<ListLabelsProps> = memo(
  ({
    labels: allLabels,
    multiline = false,
    classes: overrideClasses,
    areChipsClickable = true,
    overflowOnClick,
    updateCollapseIconVisibility,
    removeTopPadding = false
  }: ListLabelsProps) => {
    const labels = allLabels;
    const classes = useListLabelsStyles({
      removeTopPadding: removeTopPadding,
      classes: overrideClasses
    });
    const { width, ref: labelRef } = useResizeDetector();
    const [widthByLabels, setWidthByLabels] = useState<Record<string, number>>(
      {}
    );
    const [visibleChipCount, setVisibleChipCount] = useState<number>(
      labels.length
    );

    const setLabelWidth = useCallback(
      (labelId: string, width = 0) => {
        setWidthByLabels((prev) => {
          return { ...prev, [labelId]: width };
        });
      },
      [setWidthByLabels]
    );

    useEffect(() => {
      if (width) {
        let index = 0;
        let computedWidth = 0;
        for (let i = 0; i < labels.length; i++) {
          computedWidth += widthByLabels[labels[i].id];
          if (computedWidth < (width ?? 0) - 50) {
            index++;
          } else {
            break;
          }
        }
        setVisibleChipCount(index);
        updateCollapseIconVisibility?.(index < labels.length);
      }
    }, [
      updateCollapseIconVisibility,
      labels,
      visibleChipCount,
      width,
      widthByLabels,
      multiline
    ]);

    const filteredLabels = useMemo(
      () => (multiline ? labels : labels.slice(0, visibleChipCount)),
      [visibleChipCount, labels, multiline]
    );
    return (
      <div className={classes.root} ref={labelRef}>
        {filteredLabels.map((label) => (
          <ListNoteLabel
            key={label.id}
            label={label}
            isChipClickable={areChipsClickable}
            setWidth={setLabelWidth}
          />
        ))}
        {!multiline && visibleChipCount < labels.length && (
          <Chip
            className={classes.chip}
            data-testid="overflow-chip-count"
            label={`+${labels.length - visibleChipCount}`}
            onClick={overflowOnClick}
          />
        )}
      </div>
    );
  }
);
ListLabels.displayName = 'ListLabels';

type NoteLabelProps = {
  label: TimesheetLabel;
  setWidth: (labelId: string, width: number) => void;
  isChipClickable: boolean;
};

const useStyles = makeStyles((theme) => ({
  root: {
    minWidth: '50px',
    maxWidth: '200px'
  },
  container: {
    margin: theme.spacing(0.25, 0.5, 0.25, 0),
    display: 'flex',
    flexDirection: 'row',
    maxWidth: '100%'
  },
  title: {
    color: theme.palette.grey[700]
  },
  commentIcon: {
    color: theme.palette.repliconBlue.main
  }
}));

const useTooltipStyles = makeStyles((theme) => ({
  popper: {
    zIndex: theme.zIndex.tooltip + 2
  }
}));

const ListNoteLabel: React.FC<NoteLabelProps> = memo(
  ({ label, setWidth, isChipClickable }: NoteLabelProps) => {
    const classes = useStyles();

    const chipClasses = useMemo(() => ({ root: classes.root }), [classes.root]);
    const { width, ref: labelRef } = useResizeDetector();
    const tooltipClasses = useTooltipStyles();

    useEffect(() => setWidth(label.id, (width ?? 0) + 4), [
      label.id,
      label.name,
      setWidth,
      width
    ]);
    if (label.type === LabelType.Comment && !label?.value?.stringValue) {
      return <></>;
    }
    return (
      <div
        data-testid={`note-label-${label.id}`}
        className={classes.container}
        ref={labelRef}
      >
        {isLabelType(label, LabelType.Comment) ? (
          <Tooltip
            title={`${label?.name}: ${label?.value?.stringValue}`}
            classes={tooltipClasses}
          >
            <CommentIcon
              data-testid="commentIcon"
              fontSize="medium"
              className={classes.commentIcon}
            />
          </Tooltip>
        ) : (
          <ReadonlyLabelChip
            isChipClickable={isChipClickable}
            size={'small'}
            label={label}
            classes={chipClasses}
          />
        )}
      </div>
    );
  }
);

ListNoteLabel.displayName = 'ListNoteLabel';
